import React, {useState} from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import moment from "moment";
import commaNumber from "comma-number";
import dayjs from "dayjs";

const format = commaNumber.bindWith(",", ".");

const CustomerLedgerTable = ({
  printMode = false,
  // ✅ these props are only passed when rendering supplier tab
  isSupplierTab   = false,
  supplierList    = [],
  supplierOpening = 0,
  supplierClosing = 0,
}) => {
  const { list, loading, openingBalance, closingBalance } =
    useSelector((state) => state.customerLedger);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // ✅ use supplier data when in supplier tab, otherwise use main data
  const displayList    = isSupplierTab ? supplierList    : list;
  const displayOpening = isSupplierTab ? supplierOpening : openingBalance;
  const displayClosing = isSupplierTab ? supplierClosing : closingBalance;

  const totals = displayList.reduce(
    (acc, row) => {
      acc.debit  += Number(row.debit_amount  || 0);
      acc.credit += Number(row.credit_amount || 0);
      return acc;
    },
    { debit: 0, credit: 0 }
  );

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (date) => {
        if (!date) return "";
        if (typeof date === "string" && /^\d{2}-\d{2}-\d{4}$/.test(date)) {
          const [day, month, year] = date.split("-");
          return moment(`${year}-${month}-${day}`).format("YYYY-MMM-DD");
        }
        const parsed = moment(date);
        return parsed.isValid() ? parsed.format("YYYY-MMM-DD") : "";
      },
      width: 120,
    },
    {
      title: "Particular",
      dataIndex: "particular",
      width: 300,
      render: (text) => <div dangerouslySetInnerHTML={{ __html: text }} />,
    },
    {
      title: "VCH No",
      dataIndex: "vch_no",
      width: 120,
      render: (val, row) => {
        if (row.vch_type === "Advance" && val)
          return `ADV-${String(val).slice(-6).toUpperCase()}`;
        return val || "";
      },
    },
    {
      title: "VCH Type",
      dataIndex: "vch_type",
      width: 100,
    },
    // only show Due Date column in customer tab
    ...(!isSupplierTab ? [{
      title: "Due Date",
      dataIndex: "due_date",
      width: 120,
      render: (val) => {
        if (!val) return "";
        if (typeof val === "string") return val;
        return dayjs(val).format("DD-MMM-YYYY");
      },
    }] : []),
    {
      title: "Debit",
      dataIndex: "debit_amount",
      render: (val) => format(parseFloat(val || 0).toFixed(2)),
      align: "right",
      width: 120,
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      render: (val) => format(parseFloat(val || 0).toFixed(2)),
      align: "right",
      width: 120,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      render: (val) => format(parseFloat(val || 0).toFixed(2)),
      align: "right",
      width: 120,
    },
  ];

  // summary colspan — 5 for customer (has due date col), 4 for supplier
  const labelColSpan = isSupplierTab ? 4 : 5;

  if (printMode) {
  return (
    <table className="ledger-print-table">
      <thead>
        <tr className="ledger-print-heading">
          <td style={{ width: "5%" }}>No.</td>
          <td style={{ width: "10%" }}>Date</td>
          <td style={{ width: "30%" }}>Particular</td>
          <td style={{ width: "12%" }}>VCH No</td>
          <td style={{ width: "10%" }}>VCH Type</td>

          {!isSupplierTab && (
            <td style={{ width: "10%" }}>Due Date</td>
          )}

          <td style={{ width: "11%", textAlign: "right" }}>Debit</td>
          <td style={{ width: "11%", textAlign: "right" }}>Credit</td>
          <td style={{ width: "11%", textAlign: "right" }}>Balance</td>
        </tr>
      </thead>

      <tbody>
        <tr className="ledger-print-total">
          <td
            colSpan={isSupplierTab ? 5 : 6}
            style={{ textAlign: "right", fontWeight: 700 }}
          >
            Opening Balance :
          </td>

          <td style={{ textAlign: "right", fontWeight: 700 }}>
            {format(Number(displayOpening || 0).toFixed(2))}
          </td>

          <td></td>
          <td></td>
        </tr>

        {displayList.map((row, index) => (
          <tr key={`${row.sequence}-${row.id}-${index}`}>
            <td>{index + 1}</td>

            <td>
              {row.created_date
                ? moment(row.created_date).format("YYYY-MMM-DD")
                : ""}
            </td>

            <td>
              <span
                dangerouslySetInnerHTML={{
                  __html: row.particular || "",
                }}
              />
            </td>

            <td>
              {row.vch_type === "Advance" && row.vch_no
                ? `ADV-${String(row.vch_no)
                    .slice(-6)
                    .toUpperCase()}`
                : row.vch_no || ""}
            </td>

            <td>{row.vch_type || ""}</td>

            {!isSupplierTab && (
              <td>
                {row.due_date
                  ? typeof row.due_date === "string"
                    ? row.due_date
                    : dayjs(row.due_date).format("DD-MMM-YYYY")
                  : ""}
              </td>
            )}

            <td style={{ textAlign: "right" }}>
              {format(Number(row.debit_amount || 0).toFixed(2))}
            </td>

            <td style={{ textAlign: "right" }}>
              {format(Number(row.credit_amount || 0).toFixed(2))}
            </td>

            <td style={{ textAlign: "right" }}>
              {format(Number(row.balance || 0).toFixed(2))}
            </td>
          </tr>
        ))}

        <tr className="ledger-print-total">
          <td
            colSpan={isSupplierTab ? 5 : 6}
            style={{ textAlign: "right", fontWeight: 700 }}
          >
            Grand Total :
          </td>

          <td style={{ textAlign: "right", fontWeight: 700 }}>
            {format(totals.debit.toFixed(2))}
          </td>

          <td style={{ textAlign: "right", fontWeight: 700 }}>
            {format(totals.credit.toFixed(2))}
          </td>

          <td></td>
        </tr>

        <tr className="ledger-print-total">
          <td
            colSpan={isSupplierTab ? 5 : 6}
            style={{ textAlign: "right", fontWeight: 700 }}
          >
            Closing Balance :
          </td>

          <td style={{ textAlign: "right", fontWeight: 700 }}>
            {format(Number(displayClosing || 0).toFixed(2))}
          </td>

          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
  );
}
  
  return (
    <Table
      rowKey={(row, i) => `${row.sequence}-${row.id}-${row.created_date}-${i}`}
      columns={columns}
        data-testid="customer-due-ledger-table" 
      dataSource={displayList}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
        onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
      }}
      bordered
      scroll={printMode ? undefined : { x: "max-content" }}
      size={printMode ? "small" : "middle"}
      summary={() => (
        <>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={labelColSpan}>
              <strong>Opening Balance</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={labelColSpan} align="right">
              <strong>{format(parseFloat(displayOpening || 0).toFixed(2))}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={labelColSpan + 1} align="right" />
            <Table.Summary.Cell index={labelColSpan + 2} align="right" />
          </Table.Summary.Row>

          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={labelColSpan}>
              <strong>Grand Total</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={labelColSpan} align="right">
              <strong>{format(totals.debit.toFixed(2))}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={labelColSpan + 1} align="right">
              <strong>{format(totals.credit.toFixed(2))}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={labelColSpan + 2} align="right" />
          </Table.Summary.Row>

          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={labelColSpan}>
              <strong>Closing Balance</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={labelColSpan} align="right">
              <strong>{format(parseFloat(displayClosing || 0).toFixed(2))}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={labelColSpan + 1} colSpan={2} align="right" />
          </Table.Summary.Row>
        </>
      )}
    />
  );
};

export default CustomerLedgerTable;